import * as _ from 'underscore';

import * as React from 'react';
import { defaultMemoize } from 'reselect';

import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget } from '@tableau/widgets';

import { Formatters, Messages } from '../../../codegen/Localize';
import { GraphDatabase, GraphTable, mapGraphTypeToIContentInfo } from '../../../ts/mappers/DatabaseAndTableMapper';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { getDisplaySortDropdownForContentTypes } from '../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { DisplayMode } from '../../../ts/ServerApi/types/ISessionInfo';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { ItemsSet } from '../../ItemsSet';
import { SpaceInfo } from '../../state/SpaceInfo';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { AdvancedFilterPanelToggleButton } from './AdvancedFilterPanelToggleButton';
import { DatasourceViewType, DatasourceViewTypeDropdown, DatasourceViewTypeInfo } from './DatasourceViewTypeDropdown';
import { ExplorerStyles } from './ExplorerStyles';
import { ExplorerViewModeSwitcher } from './ExplorerViewModeSwitcher';
import { SortByDropdown } from './SortByDropdown';


export interface ExplorerToolbarProps {
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly isSelectionBusy: boolean;
  readonly clearAllSelections: () => void;
  readonly selectAll: () => void;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly advancedFilterButtonClickHandler: () => void;
  readonly currentViewMode: DisplayMode;
  readonly changeViewMode: (viewMode: DisplayMode) => void;
  readonly contentSort: ReadonlyArray<IOrderClause>;
  readonly contentSortSelected: (order: ReadonlyArray<IOrderClause>) => void;
  readonly showDatasourceViewTypeDropDown: boolean;
  readonly datasourceViewTypeSelected: (viewType: DatasourceViewTypeInfo) => void;
  readonly hasTextSearchParameter: boolean; // should this just be an array of filters (and below)
  readonly filtersApplied: boolean;
  readonly itemCount: number;
  readonly showAdvancedFilterButton: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly supportedDisplayModes: ReadonlyArray<DisplayMode>;
  readonly hideSelection: boolean;
  readonly contentFilterControl?: JSX.Element;
  readonly contentSpecificControls?: ContentSpecificControls;
  readonly telemetry: Telemetry;
  readonly disableButtonsOnRight?: boolean;
}

export type ContentSpecificControlPosition = 'beginning' | 'end';
export type ContentSpecificControls = { [P in ContentSpecificControlPosition]?: JSX.Element };

export class ExplorerToolbar extends React.PureComponent<ExplorerToolbarProps> {

  private memoizedGetDerivedPropsFromSelectedItems = defaultMemoize(getDerivedPropsFromSelectedItems);

  public render(): JSX.Element {

    const { selectedItemsString, selectedItemsArray } = this.memoizedGetDerivedPropsFromSelectedItems(this.props.selectedItems);

    // currently we leave most of the toolbar almost always enabled.
    // Prior logic was this.props.isSelectionBusy || this.props.itemCount === 0
    // but this lead to confusing situations with advanced filters and content type
    const disabled = this.props.disableButtonsOnRight || false;

    /** Currently content items that are selected in the explorer aren't _really_ IExplorableItemTypeInfo
     *  See the doc comment on mapGraphTypeToIContentInfo and hopefully this can be removed some day
     */
    const mappedSelectedItems = selectedItemsArray.map(
      t => t.contentType === 'database' || t.contentType === 'table'
      ? mapGraphTypeToIContentInfo(t as undefined as GraphDatabase | GraphTable)
      : t
    );

    return (

      <div className={ExplorerStyles.Toolbar} data-tb-test-id={'tb-spaces-toolbar'}>
        <div className={ExplorerStyles.ToolbarButtonGroup}>
          {this.props.contentSpecificControls && this.props.contentSpecificControls.beginning}
          {this.props.hideSelection ||
            <React.Fragment>
              <ButtonWidget buttonType={ButtonType.DarkText}
                children={Messages.selectAll()}
                disabled={this.props.isSelectionBusy || this.props.itemCount === 0}
                handleClick={this.props.selectAll}
                testId={ExplorerToolbarTestIds.SelectAllButton}
                style={ExplorerStyles.ToolbarButtonWidthPadding} />

              {(selectedItemsArray.length > 0 || this.props.isSelectionBusy) &&
                <React.Fragment>
                  <ButtonWidget buttonType={ButtonType.DarkText}
                    children={Messages.clearAll()}
                    disabled={this.props.isSelectionBusy}
                    handleClick={this.props.clearAllSelections}
                    testId={ExplorerToolbarTestIds.DeselectAllButton}
                    style={ExplorerStyles.ToolbarButtonWidthPadding} />
                  <span className={ExplorerStyles.ToolbarButtonDivider} />
                  {this.props.isSelectionBusy &&
                    <div className={ExplorerStyles.ToolbarButtonGroup}>
                      <ActivitySpinnerWidget testId={ExplorerToolbarTestIds.BusySpinner} spinnerSize={ActivitySpinnerSize.Small} />
                      <span>{Messages.selecting()}</span>
                    </div>
                  }
                  {!this.props.isSelectionBusy &&
                    <React.Fragment>
                      <span data-tb-test-id={ExplorerToolbarTestIds.SelectedItemsCount}>
                        {selectedItemsString}
                      </span>
                      <ActionsMenu targetItems={mappedSelectedItems} useIcon={false}/>
                    </React.Fragment>
                  }
                </React.Fragment>
              }
            </React.Fragment>
          }
        </div>

        <div className={ExplorerStyles.ToolbarButtonGroup}>

          {this.dataSourceViewTypeDropdown(disabled)}

          {this.props.contentFilterControl}

          {this.shouldShowContentSort() &&
            <React.Fragment>
              <span className={ExplorerStyles.toolbarDropdownLabelClass(disabled)}>{Messages.sortByDropdown_label()}</span>
              <SortByDropdown
                disabled={disabled}
                order={this.props.contentSort}
                orderSelected={this.props.contentSortSelected}
                contentTypes={this.props.contentFilter}
                spaceInfo={this.props.spaceInfo}
                hasTextSearchValue={this.props.hasTextSearchParameter}
                telemetry={this.props.telemetry}
              />
            </React.Fragment>
          }

          {this.props.supportedDisplayModes.length > 1 &&
            <ExplorerViewModeSwitcher
              disabled={disabled}
              currentViewMode={this.props.currentViewMode}
              changeViewMode={this.changeViewMode}
              telemetry={this.props.telemetry} />}

          {this.props.showAdvancedFilterButton &&
            <AdvancedFilterPanelToggleButton
              disabled={disabled}
              advancedFilterButtonClickHandler={this.props.advancedFilterButtonClickHandler}
              filtersApplied={this.props.filtersApplied}/>}
          {this.props.contentSpecificControls && this.props.contentSpecificControls.end}
        </div>
      </div>
    );
  }

  private shouldShowContentSort() {
    return getDisplaySortDropdownForContentTypes(this.props.contentFilter, this.props.spaceInfo);
  }

  private dataSourceViewTypeDropdown = (disabled: boolean): JSX.Element => {
    if (this.props.showDatasourceViewTypeDropDown) {
      return (
        <React.Fragment>
          <span className={ExplorerStyles.toolbarDropdownLabelClass(disabled)}>{Messages.dataSourceViewTypeDropdown_label()}</span>
          <DatasourceViewTypeDropdown
            disabled={disabled}
            viewTypeSelected={this.props.datasourceViewTypeSelected}
            viewType={this.props.contentFilter[0] as DatasourceViewType} />
        </React.Fragment>
      );
    }

    return null;
  }

  public changeViewMode = (viewMode: DisplayMode): void => {
    this.props.changeViewMode(viewMode);
  }
}

interface DerivedPropsThatDependOnSelectedItems {
  selectedItemsString: string;
  selectedItemsArray: IExplorableItemTypeInfo[];
}

function getDerivedPropsFromSelectedItems(selectedItems: ItemsSet<IExplorableItemTypeInfo>): DerivedPropsThatDependOnSelectedItems {

  const selectedItemsArray = _.values(selectedItems);
  const selectedItemsCount = selectedItemsArray.length;

  const selectedItemsString = Messages.selectedItems({
    COUNT: selectedItemsCount,
    COUNT_LOC: Formatters.formatCount(selectedItemsCount)
  });

  return { selectedItemsString, selectedItemsArray };
}

export namespace ExplorerToolbarTestIds {
  export const SelectedItemsCount = 'selected-items-count';
  export const SelectAllButton = 'tb-select-all';
  export const DeselectAllButton = 'tb-deselect-all';
  export const ActionsMenu = 'actions-menu';
  export const BusySpinner = 'busy';
  export const FilterByTypeDropdown = 'filter-by-type';
  export const FilterByTypeDropdownItem = 'filter-by-type';
  export const FilterPanelButton = 'advanced-filter-panel-Button';
  export const ViewModeSwitcher = 'view-mode-toggle-Button';
  export const SortByDropdown = 'content-explorer-sort-by-button-Button';
}
